/**
 * `/login` — and the path is not a preference.
 *
 * `auth-ikigai.controller.ts` hard-codes `new URL('/login', WEB_ORIGIN)` with
 * `?error=<code>` for every handshake failure, so this route must exist at exactly this
 * path and must read that parameter. The API deliberately sends a bare stable code and
 * nothing else — Ikigai's own `error_description` is written for Ikigai's error page, and
 * passing a provider's prose into ours would make an upstream string a hole in our UI.
 * Rendering it is this page's job.
 *
 * Which sign-in to offer comes from `/health`'s `identityProvider`, which is `'fake'` or
 * `'ikigai'` and nothing else. No new endpoint and no build-time flag: the route that does
 * not apply genuinely does not exist on the server, so guessing would produce a 404.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useSearchParams } from 'react-router-dom';
import { ApiRequestError, fakeLogin, getHealth } from '../api/client.js';
import { useSession } from '../auth/session.js';

/** The seeded bindings, offered so a demo needs no notes beside it. */
const SEEDED = ['platform@ikigai.dev', 'owner@demo.ikigai.dev', 'staff@demo.ikigai.dev'];

type Provider = 'fake' | 'ikigai' | 'unknown';

export function LoginPage(): React.JSX.Element {
  const { t } = useTranslation();
  const { state, refresh } = useSession();
  const [params] = useSearchParams();
  const [provider, setProvider] = useState<Provider>('unknown');
  const [email, setEmail] = useState(SEEDED[0] ?? '');
  const [failure, setFailure] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  const redirectError = params.get('error');

  useEffect(() => {
    let cancelled = false;
    getHealth()
      .then((health) => {
        if (!cancelled) setProvider(health.identityProvider);
      })
      .catch(() => {
        if (!cancelled) setProvider('unknown');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.status === 'authenticated') return <Navigate to="/" replace />;

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setFailure(undefined);
    try {
      await fakeLogin(email);
      await refresh();
    } catch (error) {
      // The code, not the message: the message is English prose from the API, and this
      // page renders Arabic first.
      setFailure(error instanceof ApiRequestError ? error.body.error : 'unexpected');
    } finally {
      setBusy(false);
    }
  }

  /**
   * A full-page navigation, never `fetch`.
   *
   * `/auth/start` answers 302 to Ikigai's authorize endpoint. A fetch would follow it
   * cross-origin and die on CORS; and only a top-level navigation carries the
   * `SameSite=Lax` initiation cookie that the callback needs to match `state` against.
   */
  function startIkigai(): void {
    window.location.assign('/api/v1/auth/start');
  }

  const shown = failure ?? redirectError;

  return (
    <section className="card card--narrow">
      <h2>{t('login.title')}</h2>

      {shown && (
        <p className="notice notice--bad" role="alert">
          {t(`error.${shown}`, { defaultValue: t('error.unexpected') })}
        </p>
      )}

      {/* Signing in again cannot fix this one, so the page says so instead of inviting a retry. */}
      {shown === 'principal_unbound' && <p className="muted">{t('login.unboundHelp')}</p>}

      {provider === 'ikigai' && (
        <>
          <p className="muted">{t('login.ikigaiLead')}</p>
          <button type="button" className="btn btn--primary" onClick={startIkigai}>
            {t('login.ikigaiButton')}
          </button>
        </>
      )}

      {provider === 'fake' && (
        <form onSubmit={(event) => void submit(event)}>
          <p className="notice notice--warn">{t('login.fakeWarning')}</p>
          <label className="field">
            <span>{t('login.emailLabel')}</span>
            <input
              type="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              list="seeded-emails"
              autoComplete="username"
            />
          </label>
          <datalist id="seeded-emails">
            {SEEDED.map((address) => (
              <option key={address} value={address} />
            ))}
          </datalist>
          <button type="submit" className="btn btn--primary" disabled={busy}>
            {busy ? t('login.working') : t('login.fakeButton')}
          </button>
        </form>
      )}

      {provider === 'unknown' && <p className="muted">{t('status.unreachable')}</p>}
    </section>
  );
}
